import { useState } from 'react';
import { useStore } from '@nanostores/react';
import { Modal, ModalBody } from '@customerio/pluma-components/react';
import { billing } from '@/services/billing';
import { INBOX_PREVIEWS_CONFIG } from '../utils/credits-purchase-config';
import type { CreditsPurchasePageName } from '../utils/credits-purchase-page-name';
import { CreditsPurchaseForm } from './credits-purchase-form';
import { InboxPreviewsOrdwayPurchaseForm } from './inbox-previews-ordway-purchase-form';

type InboxPreviewsPurchaseModalProps = {
  /** The surface that opened the modal, reported with every event the form fires. */
  pageName: CreditsPurchasePageName;
  isOpen: boolean;
  onClose: () => void;
  /** Fired after the credits are confirmed granted, so the opener can refresh balances. */
  onPurchased: () => void;
};

/**
 * The "Purchase inbox previews" modal, the same surface the AI-credits purchase uses. Which form it
 * hosts is decided by who bills the account: an invoice-billed (Ordway) account buys on its bill
 * through the Ordway form, and everyone else pays through the shared Stripe form in packs mode (fixed
 * pack radios, card + billing address, tax-inclusive total).
 *
 * The branch reads `isOrdwayVendor` — who bills this account — and not `isOrdwayCustomer`, which only
 * says an `ordway_id` exists to look something up with. The backend routes on the same vendor check, so
 * a Metronome-billed account that kept a stale `ordway_id` would be shown a form the server answers
 * with a 400. There is no client-side guard beyond this: whether the account may buy at all is the
 * opener's decision, through the shared credit-purchase gating.
 *
 * Unlike the AI-credits modal this one is controlled, not managed: the opener holds it open in the
 * URL (`?purchaseModal=inbox-previews`) so the Design Studio deep link and the in-page button are the
 * same request. While a purchase is processing the modal is non-dismissable, matching the AI-credits
 * modal. The form mounts only while the modal is open — it runs Stripe Elements, tax lookups, and
 * open/close analytics on mount, and each open should start from a fresh form.
 */
export function InboxPreviewsPurchaseModal({
  pageName,
  isOpen,
  onClose,
  onPurchased,
}: InboxPreviewsPurchaseModalProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const isOrdwayVendor = useStore(billing.isOrdwayVendor);

  const onSuccess = () => {
    onPurchased();
    onClose();
  };

  return (
    <Modal
      data-test-inbox-previews-purchase-modal
      title="Purchase inbox previews"
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isProcessing}
    >
      <ModalBody>
        {isOpen ? (
          isOrdwayVendor ? (
            <InboxPreviewsOrdwayPurchaseForm
              pageName={pageName}
              onSuccess={onSuccess}
              onCancel={onClose}
              onProcessingChange={setIsProcessing}
            />
          ) : (
            <CreditsPurchaseForm
              config={INBOX_PREVIEWS_CONFIG}
              pageName={pageName}
              onSuccess={onSuccess}
              onCancel={onClose}
              onProcessingChange={setIsProcessing}
            />
          )
        ) : null}
      </ModalBody>
    </Modal>
  );
}
